import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { copyTextToClipboard } from "@/lib/clipboard";
import { ArrowRight } from "lucide-react";
import AdBanner from "@/components/AdBanner";
import TrendChart from "@/components/dashboard/TrendChart";
import CompareSection from "@/components/dashboard/CompareSection";
import HistoryCard from "@/components/dashboard/HistoryCard";
import PriceAlerts from "@/components/dashboard/PriceAlerts";

const Dashboard = () => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catMap, setCatMap] = useState({});
  const [compare, setCompare] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/valuations");
      setItems(res.data);
    } catch (err) {
      toast.error("Error loading");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/categories").then((res) => {
      const map = {};
      (res.data.categories || []).forEach((c) => { map[c.key] = c; });
      setCatMap(map);
    }).catch(() => {});
  }, [load]);

  const catLabel = (key) => catMap[key]?.[lang] || key.replace(/_/g, " ");

  const del = async (id) => {
    try {
      await api.delete(`/valuations/${id}`);
      setItems((prev) => prev.filter((v) => v.id !== id));
      setCompare((prev) => prev.filter((x) => x !== id));
      toast.success(t.dashboard.deleted);
    } catch {
      toast.error("Error");
    }
  };

  const toggleCompare = (id) =>
    setCompare((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length < 2) return [...prev, id];
      return [prev[1], id];
    });

  const shareVal = async (id) => {
    try {
      const res = await api.post(`/valuations/${id}/share`);
      const url = `${window.location.origin}/condivisa/${res.data.share_token}`;
      await copyTextToClipboard(url);
      toast.success(t.dashboard.share_copied);
    } catch {
      toast.error("Error");
    }
  };

  const compareItems = compare.map((id) => items.find((v) => v.id === id)).filter(Boolean);

  return (
    <main data-testid="dashboard-page" className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14">
        <span className="overline text-[#002FA7]">USER / HISTORY</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">{t.dashboard.title}</h1>

        {!loading && items.length > 0 && <PriceAlerts />}

        {!loading && items.length > 0 && <TrendChart items={items} catLabel={catLabel} />}

        {compare.length === 1 && (
          <p data-testid="compare-hint" className="mt-6 font-mono text-[10px] tracking-widest uppercase text-[#737373]">{t.dashboard.compare_hint}</p>
        )}

        {compareItems.length === 2 && (
          <CompareSection compareItems={compareItems} catLabel={catLabel} onClose={() => setCompare([])} />
        )}

        {loading && <div className="mt-10 font-mono text-sm text-[#737373]">...</div>}

        {!loading && items.length === 0 && (
          <div data-testid="dashboard-empty" className="mt-10 border border-black p-12 text-center">
            <p className="text-lg">{t.dashboard.empty}</p>
            <Link to="/valutazione" className="btn-primary mt-6 inline-flex">
              {t.nav.valuate} <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((v) => (
              <HistoryCard
                key={v.id}
                item={v}
                catLabel={catLabel}
                compared={compare.includes(v.id)}
                onShare={() => shareVal(v.id)}
                onCompare={() => toggleCompare(v.id)}
                onDelete={() => del(v.id)}
              />
            ))}
          </div>
        )}

        <div className="mt-12">
          <AdBanner slot="dashboard-bottom" />
        </div>
      </div>
    </main>
  );
};

export default Dashboard;
